import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, InputAdornment, Tooltip, Typography } from '@mui/material';
import { Icon } from '@ui/Icon';
import InlineTextField from '@ui/InlineTextField';
import { Menu } from '@ui/menus/Menu';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import {
  selectScopeLevel,
  useProjectScopeTarget,
  useFallbackTargetLabel,
  useScopeLevels,
  useSelectedScopeLevel,
  useSetProjectScopeTarget,
} from '@features/scope/scope-targets.hooks';
import { presetDimensions } from '@features/scope/scope.store';
import { ScopePreset } from '@features/lifecycles/scope-presets.data';
import { usePhasesCardStyles } from './phases-card.styles';
import { parseTargetInput, sanitizeTargetInput } from './target-input.helpers';

const TARGET_INPUT_WIDTH = 200;

/**
 * Opens the scope's graded levels (Miljöbyggnad Silver or Guld). Only a caret:
 * the level it points at is already spelled out by the field's own value.
 *
 * A hand-written target is not one of the levels, so none is marked while one
 * is set — and picking a level then drops it, since the level is what the user
 * just asked to be measured against.
 */
const ScopeLevelSelect: React.FC = () => {
  const { classes, cx } = usePhasesCardStyles();
  const levels = useScopeLevels();
  const selectedLevel = useSelectedScopeLevel();
  const manualTarget = useProjectScopeTarget();
  const setProjectScopeTarget = useSetProjectScopeTarget();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const selectedLevelId =
    manualTarget === undefined ? selectedLevel?.id : undefined;

  const handleSelect = useCallback(
    (levelId: string) => {
      selectScopeLevel(levelId);
      setProjectScopeTarget(undefined);
      setAnchorEl(null);
    },
    [setProjectScopeTarget],
  );

  return (
    <>
      <Box
        component="button"
        type="button"
        className={cx(classes.targetFieldButton, classes.targetLevelCaret)}
        aria-label="Select level"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          // The caret sits inside the input; a bubbling click would put the
          // field into edit mode behind the menu.
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
      >
        <Icon
          category="mui"
          icon={anchorEl ? 'KeyboardArrowUp' : 'KeyboardArrowDown'}
          size={12}
        />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {levels.map((level) => (
          <Menu.Item
            key={level.id}
            selected={level.id === selectedLevelId}
            icon={
              level.id === selectedLevelId ? (
                <Icon category="mui" icon="Check" />
              ) : undefined
            }
            onClick={() => handleSelect(level.id)}
          >
            {level.targetName}
          </Menu.Item>
        ))}
      </Menu>
    </>
  );
};

/** Drops the hand-written target, handing the scope back to its fallback. */
const ClearTargetButton: React.FC<{ onClear: () => void }> = ({ onClear }) => {
  const { classes, cx } = usePhasesCardStyles();

  return (
    <Tooltip title="Clear" placement="bottom" arrow>
      <Box
        component="button"
        type="button"
        className={cx(classes.targetFieldButton, classes.targetClearButton)}
        aria-label="Clear target"
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          // The button sits inside the input, whose click starts editing.
          event.stopPropagation();
          onClear();
        }}
      >
        <Icon category="mui" icon="Close" size={13} />
      </Box>
    </Tooltip>
  );
};

type ProjectTargetFieldProps = {
  /** Preset being hovered in the overlay's list, previewed instead of the live scope. */
  previewPreset?: ScopePreset;
  /**
   * Bumped when the overlay was opened to edit the target — from the target
   * line in the proposals chart. Puts the field straight into edit mode, with
   * whatever it holds selected.
   */
  focusRequestId?: number;
};

/**
 * The project's climate target for the scope currently set, editable inside the
 * scope overlay. The target belongs to the scope, so switching lifecycles, SBEF
 * groups or generic carbon shows that scope's own target. Clearing the field
 * removes it and lets the fallback take over, which is what the placeholder
 * previews.
 */
export const ProjectTargetField: React.FC<ProjectTargetFieldProps> = ({
  previewPreset,
  focusRequestId = 0,
}) => {
  const { classes, cx } = usePhasesCardStyles();
  // Hovering a preset row previews what that scope would report, target
  // included; the live scope is what the field writes to.
  const previewScope = useMemo(
    () =>
      previewPreset
        ? { ...presetDimensions(previewPreset), preset: previewPreset }
        : undefined,
    [previewPreset],
  );
  // At rest the field itself shows the previewed scope's values, without the
  // focused look; mid-edit the preview is drawn over it instead.
  const previewing = previewScope !== undefined;
  const target = useProjectScopeTarget(previewScope);
  // The fallback, not the target in force: the placeholder has to say what
  // applies once the field is emptied, even while it still holds a target.
  const { levelName, value: fallbackValue } =
    useFallbackTargetLabel(previewScope);
  const hasLevels = useScopeLevels(previewScope).length > 1;
  const hasManualTarget = target !== undefined;
  const setProjectScopeTarget = useSetProjectScopeTarget();
  const [editing, setEditing] = useState(false);
  // Whether anything has been typed without being committed. The input owns the
  // text itself; this only says that there is work in it worth protecting.
  const [draft, setDraft] = useState<string>();
  const fieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusRequestId > 0) setEditing(true);
  }, [focusRequestId]);

  // An edit is never torn down by a preview: the field keeps its text, focus and
  // caret, and the preview is drawn over it instead (see targetPreviewOverlay).
  const previewingOverEdit = previewing && editing;

  // What the field reads as at rest for the scope in hand — the preview draws
  // exactly this, through the same input, so nothing can drift out of place.
  const restingValue = target === undefined ? '' : getDisplayNumber(target);

  const displayValue = editing ? String(target ?? '') : restingValue;

  // The level's name is the one part of the fallback that reads as content, so
  // at rest it sits beside the input in the field's own colour while the value
  // it stands for stays grayed out. Editing hands the whole thing back to the
  // placeholder, where the name has no weight of its own.
  const restingShowLevelName = Boolean(levelName) && !hasManualTarget;
  const showLevelName = restingShowLevelName && !editing;

  const buildPlaceholder = (withNameBeside: boolean): string => {
    if (!levelName) return fallbackValue;
    return withNameBeside
      ? `(${fallbackValue})`
      : `${levelName} (${fallbackValue})`;
  };
  const placeholder = buildPlaceholder(showLevelName);
  const restingPlaceholder = buildPlaceholder(restingShowLevelName);

  // Closing the panel takes the field with it, and an unmounted input never
  // blurs — so anything typed would be dropped. Commit it on the way out.
  const pendingCommitRef = useRef<() => void>(() => undefined);
  pendingCommitRef.current = () => {
    if (draft === undefined) return;
    setProjectScopeTarget(parseTargetInput(draft));
  };
  useEffect(() => () => pendingCommitRef.current(), []);

  // Committing with Enter leaves the input focused, so the field would keep
  // looking like it is being edited. Blur after the state has flushed, since a
  // blur while still editing would run the commit a second time.
  const stopEditing = useCallback(() => {
    setEditing(false);
    setDraft(undefined);
    requestAnimationFrame(() =>
      fieldRef.current?.querySelector('input')?.blur(),
    );
  }, []);

  const handleSave = useCallback(
    (value: string) => {
      stopEditing();
      setProjectScopeTarget(parseTargetInput(value));
    },
    [setProjectScopeTarget, stopEditing],
  );

  /**
   * The input's chrome. Taken by both the field and the preview drawn over it,
   * so the two are laid out by the very same rules.
   */
  const buildTextFieldProps = (options: {
    withLevelName: boolean;
    withButtons: boolean;
    /** Text the input sizes itself to; omitted while it fills the field. */
    autoWidthText?: string;
  }) => ({
    InputProps: {
      startAdornment: (
        <InputAdornment position="start" className={classes.targetAdornment}>
          <Icon category="costEmission" icon="kgCO2e" size={18} />
          {options.withLevelName && (
            <Typography component="span" className={classes.targetLevelName}>
              {levelName}
            </Typography>
          )}
        </InputAdornment>
      ),
      // Both buttons write to the live scope, so they have no business over a
      // preview of another one.
      endAdornment:
        options.withButtons && (hasLevels || hasManualTarget) ? (
          <InputAdornment
            position="end"
            className={cx(
              classes.targetAdornment,
              classes.targetLevelAdornment,
            )}
          >
            {hasManualTarget && (
              <ClearTargetButton
                onClear={() => setProjectScopeTarget(undefined)}
              />
            )}
            {hasLevels && <ScopeLevelSelect />}
          </InputAdornment>
        ) : undefined,
      inputProps: {
        'aria-label': 'Target (kgCO2e per m² GFA)',
        // At rest the input is only as wide as its digits, so what follows sits
        // right after the number; `ch` is the width of a digit in this font.
        // Editing hands the full field back to the input.
        style: options.autoWidthText
          ? {
              width: `${Math.max(options.autoWidthText.length, 1)}ch`,
              flexGrow: 0,
            }
          : undefined,
      },
    },
  });

  const shownText = displayValue || placeholder;
  const textFieldProps = buildTextFieldProps({
    withLevelName: showLevelName,
    withButtons: !previewing,
    autoWidthText: editing ? undefined : shownText,
  });

  return (
    <Box className={classes.controlSection}>
      <Typography className={classes.sectionLabel}>
        Target (kgCO
        <Box component="sub" className={classes.sectionLabelSub}>
          2
        </Box>
        e per m
        <Box component="sup" className={classes.sectionLabelSup}>
          2
        </Box>{' '}
        GFA)
      </Typography>
      <Box
        ref={fieldRef}
        className={cx(
          classes.targetField,
          previewing && classes.targetFieldPreview,
        )}
        sx={{ width: TARGET_INPUT_WIDTH }}
      >
        <InlineTextField
          value={displayValue}
          defaultValue={placeholder}
          // ArrowRight fills the value the placeholder stands for, not its level name.
          fillValue={fallbackValue}
          editing={editing}
          fullWidth
          selectOnFocus
          onClick={() => setEditing(true)}
          onChange={setDraft}
          onSave={handleSave}
          onCancel={stopEditing}
          formatInput={sanitizeTargetInput}
          textFieldProps={textFieldProps}
        />
        {previewingOverEdit && (
          // The edit underneath keeps its text, focus and caret; this is the
          // same input again, at rest, showing what the hovered scope reports.
          <Box className={classes.targetPreviewOverlay} aria-hidden>
            <InlineTextField
              value={restingValue}
              defaultValue={restingPlaceholder}
              editing={false}
              readonly
              fullWidth
              textFieldProps={buildTextFieldProps({
                withLevelName: restingShowLevelName,
                withButtons: false,
                autoWidthText: restingValue || restingPlaceholder,
              })}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
